/**
 * «تصدير» — any table, out as Excel or CSV (المرحلة 1.1, P21).
 *
 * The screen hands over what it is showing: a title, its columns (the national
 * ID and phone columns marked `mask: true`) and its rows. POST /api/exports
 * builds the file and applies the institution's rules — the `exports` key, who
 * may export (`exportRules.who`, DATA_EXPORT) and whether the sensitive
 * numbers are cut to their last digits — and records the export in the access
 * log. The button is not shown where the server would refuse it.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/common';
import { exportApi } from '../../api/endpoints';
import { useAuth, ROLES } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { exportFileName, saveBlob } from '../../domain/files';

/** May this session export at all? The same answer the server gives. */
export function useExportAllowed() {
  const { role } = useAuth();
  const { can, serviceShown, servicePolicy, institutionId, loading } = usePermissions();
  if (loading) return false;
  if (role === ROLES.ADMIN) return true;
  // A patient or an independent doctor: their own lists, under the platform's rules.
  if (!institutionId) return true;
  if (!serviceShown('exports')) return false;
  const rules = servicePolicy('exportRules') || {};
  return can('DATA_EXPORT') || rules.who === 'desks';
}

export function ExportButton({ title, source, columns, rows, getRows, disabled = false, className = '', compact = false }) {
  const allowed = useExportAllowed();
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrap.current && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!allowed) return null;

  const run = async (format) => {
    setOpen(false);
    setBusy(format);
    try {
      const data = getRows ? await getRows() : rows || [];
      const answer = await exportApi.table({ title, source, format, columns, rows: data });
      saveBlob(answer.blob, exportFileName(title, format));
      success(answer.masked && columns.some((c) => c.mask) ? `${t('export.done', { n: answer.rows })} · ${t('export.masked')}` : t('export.done', { n: answer.rows }));
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const empty = !getRows && (!rows || !rows.length);

  return (
    <div ref={wrap} className={`export-btn ${className}`}>
      <button type="button" className="btn-ghost export-btn__main" disabled={disabled || empty || Boolean(busy)} onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} data-testid="export-button">
        <Icon name={busy ? 'loader-2' : 'file-spreadsheet'} size={16} className={busy ? 'animate-spin' : 'text-emerald-400'} />
        {!compact && t('export.button')}
        <Icon name="chevron-down" size={14} className="text-slate-400" />
      </button>
      {open && (
        <div className="export-btn__menu" role="menu">
          <button type="button" role="menuitem" onClick={() => run('xlsx')} data-testid="export-xlsx">
            <span className="export-btn__fmt export-btn__fmt--xlsx">XLSX</span>
            {t('export.excel')}
          </button>
          <button type="button" role="menuitem" onClick={() => run('csv')} data-testid="export-csv">
            <span className="export-btn__fmt">CSV</span>
            {t('export.csv')}
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportButton;
